import { DeleteConfirmationDialog } from '@components/dialogs/DeleteConfirmationDialog'
import {
  Box,
  Dialog,
  IconButton,
  InputBase,
  Paper,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'
import { DataGridPro, GridColumns, GridRowId } from '@mui/x-data-grid-pro'
import { Actions } from '@state/actions'
import { removeClients } from '@state/reducers/clientReducer'
import { removeMembers } from '@state/reducers/memberReducer'
import { removeRoles } from '@state/reducers/roleReducer'
import { removeSites } from '@state/reducers/siteReducer'
import { useSelector } from '@state/store'
import { findAll as findAllClient } from '@state/thunks/clientThunk'
import {
  deleteMember,
  doFindMembers as findAllMember,
} from '@state/thunks/memberThunk'
import { findAll as findAllSites } from '@state/thunks/siteThunk'
import {
  EditIcon,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  UserIcon,
  Users,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import { debounce } from 'lodash'
import AssignRoleToMemberFrom from './AssignRoleToMemberFrom'
import { MemberForm } from './MemberForm'

export default function Members() {
  const dispatch = useDispatch()
  const intl = useIntl()
  const theme = useTheme()
  const [openAddDialog, setOpenAddDialog] = useState(false)
  const [openModifyDialog, setOpenModifyDialog] = useState(false)
  const [openAssignRoleDialog, setOpenAssignRoleDialog] = useState(false)
  const [selectedRowId, setSelectedRowId] = useState<number | null>(null)
  const [selectedRows, setSelectedRows] = useState<GridRowId[]>([])
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)

  const { client, members, loading, totalCount } = useSelector(
    ({ member, auth, loading }) => ({
      members: member.members.datas,
      totalCount: member.members.totalCount,
      client: auth.account?.client,
      loading:
        loading[Actions.MEMBER_FIND_ALL] ||
        loading[Actions.MEMBER_CREATE] ||
        loading[Actions.MEMBER_UPDATE],
    }),
  )

  const row = members.find((m) => m.id === selectedRowId) ?? ({} as any)

  const columns: GridColumns = useMemo(
    () => [
      {
        field: 'firstName',
        headerName: intl.formatMessage({ id: 'firstName' }),
        flex: 1,
        minWidth: 130,
      },
      {
        field: 'lastName',
        headerName: intl.formatMessage({ id: 'lastName' }),
        flex: 1,
        minWidth: 130,
      },
      {
        field: 'memberJob',
        headerName: 'Position',
        flex: 1,
        minWidth: 130,
        valueGetter: ({ row }) => row.role,
      },
      {
        field: 'email',
        headerName: 'Email',
        flex: 1.5,
        minWidth: 220,
      },
      {
        field: 'phoneNumber',
        headerName: intl.formatMessage({ id: 'phoneNumber' }),
        flex: 1,
        minWidth: 160,
      },
      {
        field: 'address',
        headerName: 'Address',
        flex: 1.5,
        minWidth: 180,
      },
      {
        field: 'actions',
        headerName: '',
        width: 100,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Box sx={{ display: 'flex', gap: 0.25 }}>
            <Tooltip title="Modifier" arrow>
              <IconButton
                size="small"
                onClick={() => {
                  setSelectedRowId(params.row.id)
                  setOpenModifyDialog(true)
                }}
                sx={{
                  color: theme.palette.text.secondary,
                  '&:hover': {
                    color: theme.palette.info.main,
                    bgcolor: alpha(theme.palette.info.main, 0.08),
                  },
                }}
              >
                <EditIcon size={17} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Assigner rôle" arrow>
              <IconButton
                size="small"
                onClick={() => {
                  setSelectedRowId(params.row.id)
                  setOpenAssignRoleDialog(true)
                }}
                sx={{
                  color: theme.palette.text.secondary,
                  '&:hover': {
                    color: theme.palette.secondary.main,
                    bgcolor: alpha(theme.palette.secondary.main, 0.08),
                  },
                }}
              >
                <UserIcon size={17} />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [intl, theme],
  )

  const debouncedSearch = useMemo(
    () =>
      debounce((search: string) => {
        setPage(0)
        dispatch(
          findAllMember({
            search: search || undefined,
            limit: pageSize,
            skip: 0,
          }),
        )
      }, 400),
    [dispatch, pageSize],
  )

  const onRefresh = useCallback(() => {
    dispatch(
      findAllMember({
        search: searchTerm || undefined,
        limit: pageSize,
        skip: page * pageSize,
      }),
    )
    if (!client) dispatch(findAllClient())
    else dispatch(findAllSites())
  }, [dispatch, client, searchTerm, page, pageSize])

  useEffect(() => {
    if (!client) dispatch(findAllClient())
    else dispatch(findAllSites())

    return () => {
      debouncedSearch.cancel()
      dispatch(removeMembers())
      dispatch(removeClients())
      dispatch(removeSites())
      dispatch(removeRoles())
    }
  }, [])

  useEffect(() => {
    dispatch(
      findAllMember({
        search: searchTerm || undefined,
        limit: pageSize,
        skip: page * pageSize,
      }),
    )
  }, [dispatch, page, pageSize])

  const _onSelectionChange = (selection: GridRowId[]) => {
    setSelectedRows(selection)
  }

  const _onDeleteConfirm = () => {
    dispatch(deleteMember(selectedRows as number[]))
    setIsDeleteDialogOpen(false)
  }

  return (
    <Box sx={{ height: '100%', p: 2 }}>
      <Paper
        elevation={0}
        sx={{
          height: 'calc(100vh - 80px)',
          width: '100%',
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2.5,
            py: 1.5,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                color: theme.palette.primary.main,
              }}
            >
              <Users size={18} />
            </Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Membres
            </Typography>
            <Typography
              variant="caption"
              sx={{
                px: 1,
                py: 0.25,
                borderRadius: 1,
                bgcolor: alpha(theme.palette.text.primary, 0.06),
                color: theme.palette.text.secondary,
                fontWeight: 500,
              }}
            >
              {totalCount ?? members.length}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 0.5,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.text.primary, 0.04),
              border: '1px solid transparent',
              transition: 'all 0.15s',
              '&:focus-within': {
                bgcolor: theme.palette.background.paper,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
              },
              minWidth: 360,
              maxWidth: 500,
            }}
          >
            <Search size={16} color={theme.palette.text.disabled} />
            <InputBase
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value)
                debouncedSearch(e.target.value)
              }}
              sx={{
                flex: 1,
                fontSize: '0.85rem',
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Rafraîchir" arrow>
              <IconButton size="small" onClick={onRefresh}>
                <RefreshCw size={17} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Supprimer" arrow>
              <IconButton
                size="small"
                onClick={() =>
                  selectedRows.length
                    ? setIsDeleteDialogOpen(true)
                    : alert('Veuillez sélectionner au moins un membre')
                }
                sx={{
                  '&:hover': {
                    color: theme.palette.error.main,
                    bgcolor: alpha(theme.palette.error.main, 0.08),
                  },
                }}
              >
                <Trash2 size={17} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Ajouter un membre" arrow>
              <IconButton
                size="small"
                onClick={() => setOpenAddDialog(true)}
                sx={{
                  color: theme.palette.primary.main,
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.16),
                  },
                }}
              >
                <Plus size={17} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <DataGridPro
          rows={members}
          columns={columns}
          loading={loading}
          checkboxSelection
          disableSelectionOnClick
          paginationMode="server"
          rowCount={totalCount ?? 0}
          page={page}
          pageSize={pageSize}
          onPageChange={(p) => setPage(p)}
          onPageSizeChange={(s) => {
            setPage(0)
            setPageSize(s)
          }}
          onSelectionModelChange={_onSelectionChange}
          selectionModel={selectedRows}
          density="comfortable"
          pagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{
            flex: 1,
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              bgcolor: alpha(theme.palette.text.primary, 0.02),
              borderBottom: `1px solid ${theme.palette.divider}`,
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 600,
              fontSize: '0.8rem',
              color: theme.palette.text.secondary,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            },
            '& .MuiDataGrid-row': {
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.03),
              },
            },
            '& .MuiDataGrid-cell': {
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              fontSize: '0.875rem',
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: `1px solid ${theme.palette.divider}`,
            },
          }}
        />
      </Paper>

      <Dialog
        open={openModifyDialog}
        onClose={() => setOpenModifyDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <MemberForm onClose={() => setOpenModifyDialog(false)} row={row} />
      </Dialog>

      <Dialog
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <MemberForm onClose={() => setOpenAddDialog(false)} />
      </Dialog>

      <DeleteConfirmationDialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={_onDeleteConfirm}
        title="Confirmer la suppression"
        description="Êtes-vous sûr de vouloir supprimer les membres sélectionnés ?"
      />

      <Dialog
        open={openAssignRoleDialog}
        onClose={() => setOpenAssignRoleDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <AssignRoleToMemberFrom
          onClose={() => setOpenAssignRoleDialog(false)}
          row={row}
        />
      </Dialog>
    </Box>
  )
}